import LoginIcon from "@mui/icons-material/Login";
import LogoutIcon from "@mui/icons-material/Logout";
import { Chip, Stack, Typography } from "@mui/material";
import type { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { type FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { EmptyState } from "../../../components/UIToolkit/molecules/EmptyState";
import { SettingsSection } from "../../../components/UIToolkit/molecules/SettingsSection";
import { SystemDataGrid } from "../../../components/UIToolkit/Tables/SystemDataGrid";
import type { PbjMissingPunchRow } from "../../../utils/models";
import { usePbjMissingPunches } from "../hooks/usePbjAudit";
import { dataTableSx, sectionCardSx } from "../PredictionTab/colors";
import { boldNameColumn, hoursColumn, monospaceIdColumn } from "../pbjGridCells";
import { formatAuditDate } from "../pbjGridFormat";

type Props = {
	startDate: string;
	endDate: string;
};

/** Unique grid id — the same (emp, date, job) can repeat across rows, so fold in the array index. */
type MissingPunchGridRow = PbjMissingPunchRow & { __rowId: string };

/**
 * Missing Punches audit — shifts missing a clock-in and/or clock-out punch. Migrated to the canonical
 * SystemDataGrid (opaque pinned header, skeleton loader, internal scroll) from a hand-rolled MUI table.
 */
export const MissingPunchesTable: FC<Props> = ({ startDate, endDate }) => {
	const { t } = useTranslation();
	const { data, isLoading, isError } = usePbjMissingPunches(startDate, endDate);

	const rows = useMemo<MissingPunchGridRow[]>(
		() => (data ?? []).map((r, i) => ({ ...r, __rowId: `${i}|${r.empId}|${String(r.date)}|${r.jobDescription}` })),
		[data],
	);

	const columns: GridColDef<MissingPunchGridRow>[] = useMemo(
		() => [
			monospaceIdColumn<MissingPunchGridRow>("empId", t("pbj_model.audit.missing_punches.cols.id")),
			boldNameColumn<MissingPunchGridRow>("name", t("pbj_model.audit.missing_punches.cols.name")),
			{
				field: "jobDescription",
				headerName: t("pbj_model.audit.missing_punches.cols.job_description"),
				minWidth: 160,
				flex: 1,
				valueFormatter: (value: string) => value || "—",
			},
			{
				field: "date",
				headerName: t("pbj_model.audit.missing_punches.cols.date"),
				minWidth: 130,
				flex: 1,
				valueFormatter: (value) => formatAuditDate(value),
			},
			hoursColumn<MissingPunchGridRow>("hours", t("pbj_model.audit.missing_punches.cols.hours"), {
				// A row missing a punch has no computable duration (the BE sends 0), so an em dash is
				// honest where "0.00" would read as a real zero-hour shift.
				valueFormatter: (value: number, row: MissingPunchGridRow) => {
					if (row.missingPunchIn || row.missingPunchOut) return "—";
					return value == null ? "" : value.toFixed(2);
				},
			}),
			{
				field: "missing",
				headerName: t("pbj_model.audit.missing_punches.cols.missing"),
				minWidth: 180,
				flex: 1,
				sortable: false,
				renderCell: (params: GridRenderCellParams<MissingPunchGridRow>) => {
					const { missingPunchIn, missingPunchOut, empId, date } = params.row;
					const key = `${empId}-${String(date)}`;
					return (
						<Stack direction="row" spacing={0.5} sx={{ alignItems: "center", height: "100%" }}>
							{missingPunchIn ? (
								<Chip
									size="small"
									color="warning"
									variant="outlined"
									icon={<LoginIcon sx={{ fontSize: 14 }} />}
									label={t("pbj_model.audit.missing_punches.flag.in")}
									data-testid={`pbj-model-audit-missing-punches-in-${key}`}
								/>
							) : null}
							{missingPunchOut ? (
								<Chip
									size="small"
									color="warning"
									variant="outlined"
									icon={<LogoutIcon sx={{ fontSize: 14 }} />}
									label={t("pbj_model.audit.missing_punches.flag.out")}
									data-testid={`pbj-model-audit-missing-punches-out-${key}`}
								/>
							) : null}
							{!missingPunchIn && !missingPunchOut ? (
								<Typography variant="body2" color="text.secondary">
									—
								</Typography>
							) : null}
						</Stack>
					);
				},
			},
		],
		[t],
	);

	return (
		<SettingsSection
			layout="stack"
			title={t("pbj_model.audit.missing_punches.section.title")}
			description={t("pbj_model.audit.missing_punches.section.subtitle")}
			dataTestId="pbj-missing-punches-section"
			sx={(theme) => sectionCardSx(theme)}
		>
			<SystemDataGrid<MissingPunchGridRow>
				rows={rows}
				columns={columns}
				getRowId={(r) => r.__rowId}
				searchableColumns={["empId", "name"]}
				searchPlaceholder={t("pbj_model.audit.missing_punches.search")}
				loading={isLoading}
				error={isError}
				errorMessage={t("pbj_model.audit.missing_punches.failed")}
				emptyState={<EmptyState title={t("pbj_model.audit.missing_punches.empty")} dataTestId="pbj-model-audit-missing-punches-empty" />}
				defaultPageSize={25}
				containedHeight
				sx={(theme) => dataTableSx(theme)}
				dataTestId="pbj-model-audit-missing-punches"
			/>
		</SettingsSection>
	);
};
